import { useCallback } from "react";
import { prunePrimarySelectionIfStaleAfterSceneGraphMutation } from "../helpers";

export function useMeshGeomSiteContextMenuActions({
  bodies,
  contextMenuMeshIds,
  contextMenuGeomKeys,
  contextMenuSiteIds,
  sceneActions,
  clearBatchSelection,
  runGroupedBodyAction,
}) {
  const handleDeleteMeshes = useCallback(
    (meshIds) => {
      if (!Array.isArray(meshIds) || meshIds.length === 0) return;
      runGroupedBodyAction(() => {
        meshIds.forEach((meshId) => {
          sceneActions.removeMesh(meshId);
        });
      });
      prunePrimarySelectionIfStaleAfterSceneGraphMutation();
      clearBatchSelection();
    },
    [runGroupedBodyAction, sceneActions, clearBatchSelection]
  );

  const handleMoveMeshesToBody = useCallback(
    (targetBodyUuid, meshIds = contextMenuMeshIds) => {
      if (!targetBodyUuid || !Array.isArray(meshIds) || meshIds.length === 0) return;
      runGroupedBodyAction(() => {
        meshIds.forEach((meshId) => {
          sceneActions.moveMeshToBody(meshId, targetBodyUuid);
        });
      });
      clearBatchSelection();
    },
    [contextMenuMeshIds, runGroupedBodyAction, sceneActions, clearBatchSelection]
  );

  const resolveGeomByKey = useCallback(
    (geomKey) => {
      if (!geomKey) return null;
      const keyString = String(geomKey);
      const sep = keyString.indexOf(":");
      const hintedBodyUuid = sep > 0 ? keyString.slice(0, sep) : null;
      const geomUuid = sep > 0 ? keyString.slice(sep + 1) : keyString;
      if (!geomUuid) return null;

      if (hintedBodyUuid && bodies?.[hintedBodyUuid]?.geoms) {
        const hintedGeom = bodies[hintedBodyUuid].geoms.find(
          (geom) => geom && String(geom.uuid) === geomUuid
        );
        if (hintedGeom) {
          return { sourceBodyUuid: hintedBodyUuid, geom: hintedGeom };
        }
      }

      for (const body of Object.values(bodies || {})) {
        if (!body?.uuid || !Array.isArray(body.geoms)) continue;
        const found = body.geoms.find((geom) => geom && String(geom.uuid) === geomUuid);
        if (found) {
          return { sourceBodyUuid: body.uuid, geom: found };
        }
      }
      return null;
    },
    [bodies]
  );

  const handleDeleteGeoms = useCallback(
    (geomKeys) => {
      if (!Array.isArray(geomKeys) || geomKeys.length === 0) return;
      runGroupedBodyAction(() => {
        geomKeys.forEach((geomKey) => {
          const resolved = resolveGeomByKey(geomKey);
          if (!resolved?.sourceBodyUuid || !resolved?.geom?.uuid) return;
          sceneActions.removeGeom(resolved.sourceBodyUuid, String(resolved.geom.uuid));
        });
      });
      prunePrimarySelectionIfStaleAfterSceneGraphMutation();
      clearBatchSelection();
    },
    [runGroupedBodyAction, resolveGeomByKey, sceneActions, clearBatchSelection]
  );

  const handleMoveGeomsToBody = useCallback(
    (targetBodyUuid, geomKeys = contextMenuGeomKeys) => {
      if (!targetBodyUuid || !Array.isArray(geomKeys) || geomKeys.length === 0) return;
      runGroupedBodyAction(() => {
        geomKeys.forEach((geomKey) => {
          const resolved = resolveGeomByKey(geomKey);
          if (!resolved?.sourceBodyUuid || !resolved?.geom) return;
          if (resolved.sourceBodyUuid === targetBodyUuid) return;
          sceneActions.addGeom({ ...resolved.geom }, targetBodyUuid);
          sceneActions.removeGeom(resolved.sourceBodyUuid, String(resolved.geom.uuid));
        });
      });
      clearBatchSelection();
    },
    [contextMenuGeomKeys, runGroupedBodyAction, resolveGeomByKey, sceneActions, clearBatchSelection]
  );

  const handleDeleteSites = useCallback(
    (siteIds) => {
      if (!Array.isArray(siteIds) || siteIds.length === 0) return;
      runGroupedBodyAction(() => {
        siteIds.forEach((siteId) => {
          sceneActions.removeSite(siteId);
        });
      });
      prunePrimarySelectionIfStaleAfterSceneGraphMutation();
      clearBatchSelection();
    },
    [runGroupedBodyAction, sceneActions, clearBatchSelection]
  );

  const handleMoveSitesToBody = useCallback(
    (targetBodyUuid, siteIds = contextMenuSiteIds) => {
      if (!targetBodyUuid || !Array.isArray(siteIds) || siteIds.length === 0) return;
      runGroupedBodyAction(() => {
        siteIds.forEach((siteId) => {
          sceneActions.moveSiteToBody(siteId, targetBodyUuid);
        });
      });
      clearBatchSelection();
    },
    [contextMenuSiteIds, runGroupedBodyAction, sceneActions, clearBatchSelection]
  );

  return {
    handleDeleteMeshes,
    handleMoveMeshesToBody,
    handleDeleteGeoms,
    handleMoveGeomsToBody,
    handleDeleteSites,
    handleMoveSitesToBody,
  };
}
